import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api.js';

function emptyForm() { return { email: '', name: '', password: '', role: 'AGENT' }; }

function StatCard({ label, value, sublabel, accent = false }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-cyan-400' : 'text-zinc-100'}`}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1">{sublabel}</div>}
    </div>
  );
}

export default function UsersAdmin() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [tab, setTab] = useState('active'); // 'active' | 'imported'
  const [search, setSearch] = useState('');
  const [reconciling, setReconciling] = useState(null); // the placeholder user being reconciled
  const [reconcileForm, setReconcileForm] = useState({ email: '', name: '', password: '', role: 'AGENT' });
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const reload = () => {
    api.get('/api/users').then((res) => setUsers(res.data.users)).catch((err) => toast.error(err.message));
  };
  useEffect(reload, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/users', form);
      toast.success(t('users.created'));
      setCreating(false); setForm(emptyForm()); reload();
    } catch (err) { toast.error(err.message); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/api/users/${u.id}`, { active: !u.active });
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/api/users/${u.id}`, { role });
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const stats = useMemo(() => {
    const active = users.filter((u) => !u.imported);
    const imported = users.filter((u) => u.imported);
    const approvers = imported.filter((u) => (u._count?.posApproved ?? 0) > 0);
    const requesters = imported.filter((u) => (u._count?.posApproved ?? 0) === 0 && (u._count?.posRequested ?? 0) > 0);
    return {
      activeCount: active.length,
      importedCount: imported.length,
      approversCount: approvers.length,
      requestersCount: requesters.length,
    };
  }, [users]);

  const filtered = useMemo(() => {
    const list = users.filter((u) => (tab === 'imported' ? u.imported : !u.imported));
    const q = search.trim().toLowerCase();
    const matched = q
      ? list.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      : list;
    if (tab === 'imported') {
      // Sort imported by approval count desc, then requests desc — heaviest signal first.
      return [...matched].sort((a, b) =>
        (b._count?.posApproved ?? 0) - (a._count?.posApproved ?? 0) ||
        (b._count?.posRequested ?? 0) - (a._count?.posRequested ?? 0)
      );
    }
    return matched;
  }, [users, tab, search]);

  const openReconcile = (u) => {
    setReconcileForm({ email: '', name: u.name || '', password: '', role: u.role || 'AGENT' });
    setReconciling(u);
  };

  const submitReconcile = async (e) => {
    e.preventDefault();
    setReconcileBusy(true);
    try {
      await api.post(`/api/users/${reconciling.id}/reconcile`, reconcileForm);
      toast.success(t('users.reconciled'));
      setReconciling(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReconcileBusy(false);
    }
  };

  return (
    <div className="px-8 py-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('users.title')}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{t('users.subtitle')}</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">{t('users.new')}</button>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label={t('users.stat.active')} value={stats.activeCount} accent />
        <StatCard label={t('users.stat.toReconcile')} value={stats.importedCount} />
        <StatCard label={t('users.stat.approvers')} value={stats.approversCount} />
        <StatCard label={t('users.stat.requesters')} value={stats.requestersCount} />
      </div>

      {/* Tabs + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/40 p-0.5 text-xs">
          {[
            { id: 'active',   label: `${t('users.tab.active')} (${stats.activeCount})` },
            { id: 'imported', label: `${t('users.tab.imported')} (${stats.importedCount})` },
          ].map((x) => (
            <button
              key={x.id}
              onClick={() => setTab(x.id)}
              className={`px-3 py-1.5 rounded transition-colors ${
                tab === x.id
                  ? 'bg-zinc-800 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder={t('users.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-xs"
        />
        <div className="ml-auto text-xs text-zinc-500 tabular-nums">
          {filtered.length} / {tab === 'imported' ? stats.importedCount : stats.activeCount}
        </div>
      </div>

      {/* Create form */}
      {creating && (
        <form onSubmit={create} className="card p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="label">{t('users.form.name')}</label>
              <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="label">{t('users.form.email')}</label>
              <input type="email" required className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="label">{t('users.form.password')}</label>
              <input type="password" required minLength={12} className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><label className="label">{t('users.form.role')}</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
                <option value="AGENT">Agent</option>
                <option value="ADMIN">Admin</option>
              </select></div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="btn-ghost">{t('common.cancel')}</button>
            <button type="submit" className="btn-primary">{t('common.save')}</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2.5 text-left">{t('users.col.name')}</th>
              <th className="px-4 py-2.5 text-left">{t('users.col.email')}</th>
              {tab === 'imported'
                ? <th className="px-4 py-2.5 text-left">{t('users.col.history')}</th>
                : <th className="px-4 py-2.5 text-left">{t('users.col.role')}</th>}
              <th className="px-4 py-2.5 text-left">{t('users.col.status')}</th>
              <th className="px-4 py-2.5 w-px"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500">
                {tab === 'imported' ? t('users.emptyImported') : t('users.emptyActive')}
              </td></tr>
            )}
            {filtered.map((u) => {
              const approved = u._count?.posApproved ?? 0;
              const requested = u._count?.posRequested ?? 0;
              return (
                <tr key={u.id} className="group border-t border-zinc-800 hover:bg-zinc-900/60 transition-colors">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{u.email}</td>
                  {tab === 'imported' ? (
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-wrap gap-2">
                        {approved > 0 && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 bg-emerald-900/30 text-emerald-300 border border-emerald-800/60">
                            {t('users.history.approved', { count: approved })}
                          </span>
                        )}
                        {requested > 0 && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 bg-zinc-800 text-zinc-300">
                            {t('users.history.requested', { count: requested })}
                          </span>
                        )}
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-3">
                      <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="input text-xs max-w-[120px]">
                        <option value="AGENT">Agent</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {tab === 'imported' ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] bg-amber-900/30 text-amber-300 border border-amber-800/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />placeholder
                      </span>
                    ) : (
                      <button onClick={() => toggleActive(u)} className={`text-xs ${u.active ? 'text-emerald-400' : 'text-zinc-600'}`}>
                        {u.active ? '● ' + t('common.active') : '○ ' + t('common.inactive')}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {tab === 'imported' && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openReconcile(u)} className="text-xs text-cyan-400 hover:text-cyan-300">
                          {t('users.reconcile')} →
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reconcile modal */}
      {reconciling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !reconcileBusy && setReconciling(null)}>
          <form
            onSubmit={submitReconcile}
            onClick={(e) => e.stopPropagation()}
            className="card w-full max-w-md p-5 space-y-4"
          >
            <div>
              <h2 className="text-base font-semibold">{t('users.reconcileTitle', { name: reconciling.name })}</h2>
              <p className="text-xs text-zinc-500 mt-1">{t('users.reconcileHint')}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label">{t('users.form.name')}</label>
                <input required className="input" value={reconcileForm.name}
                  onChange={(e) => setReconcileForm({ ...reconcileForm, name: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('users.form.email')}</label>
                <input type="email" required className="input" value={reconcileForm.email}
                  onChange={(e) => setReconcileForm({ ...reconcileForm, email: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('users.form.password')}</label>
                <input type="password" required minLength={12} className="input" value={reconcileForm.password}
                  onChange={(e) => setReconcileForm({ ...reconcileForm, password: e.target.value })} />
                <p className="text-[11px] text-zinc-500 mt-1">{t('register.passwordHint')}</p>
              </div>
              <div>
                <label className="label">{t('users.form.role')}</label>
                <select value={reconcileForm.role} onChange={(e) => setReconcileForm({ ...reconcileForm, role: e.target.value })} className="input">
                  <option value="AGENT">Agent</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setReconciling(null)} className="btn-ghost" disabled={reconcileBusy}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn-primary" disabled={reconcileBusy}>
                {reconcileBusy ? t('common.saving') : t('users.reconcile')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
